/**
 * Трассировка логики от выбранной работы (ПЛ-26), пути резерва (ПЛ-9) и раскладка сетевого графика.
 * Всё считается в браузере тем же ядром, что и на сервере.
 */
import { computeSchedule, floatPaths } from '@epc/contracts';
import { engineInput, type View } from './model';

export type TraceDir = 'back' | 'fwd' | 'both';
export interface Trace {
  id: string;
  dir: TraceDir;
  drivingOnly: boolean;
  /** Сколько шагов по связям; 0 — без ограничения */
  depth: number;
}

/** Работы, связанные с выбранной логикой назад (предшественники) и/или вперёд (последователи) */
export function traceLogic(v: View, t: Trace): Map<string, number> {
  const own = new Set(v.activities.map((a) => a.id));
  const preds = new Map<string, string[]>();
  const succs = new Map<string, string[]>();
  for (const r of v.relations) {
    if (t.drivingOnly && !r.driving) continue;
    if (!own.has(r.predId) || !own.has(r.succId)) continue;
    (preds.get(r.succId) ?? preds.set(r.succId, []).get(r.succId)!).push(r.predId);
    (succs.get(r.predId) ?? succs.set(r.predId, []).get(r.predId)!).push(r.succId);
  }
  // Шаг назад — отрицательный, вперёд — положительный
  const out = new Map<string, number>([[t.id, 0]]);
  const walk = (next: Map<string, string[]>, sign: 1 | -1) => {
    let front = [t.id];
    let step = 0;
    while (front.length && (!t.depth || step < t.depth)) {
      step++;
      const n: string[] = [];
      for (const id of front)
        for (const x of next.get(id) ?? [])
          if (!out.has(x)) {
            out.set(x, sign * step);
            n.push(x);
          }
      front = n;
    }
  };
  if (t.dir !== 'fwd') walk(preds, -1);
  if (t.dir !== 'back') walk(succs, 1);
  return out;
}

/** Пути резерва к выбранной работе: первый — ведущий (наименьший резерв), дальше — по возрастанию */
export function pathsTo(v: View, targetId: string, max = 10): string[][] {
  const { acts, rels, opts } = engineInput(v);
  const res = computeSchedule(acts, rels, opts);
  const own = new Set(v.activities.map((a) => a.id));
  const seen = new Set<string>();
  const out: string[][] = [];
  // Внешние работы других проектов из путей убираются; одинаковые после этого пути — один раз
  for (const p of floatPaths(acts, rels, res, targetId, max)) {
    const mine = p.filter((id) => own.has(id));
    const key = mine.join();
    if (!mine.length || seen.has(key)) continue;
    seen.add(key);
    out.push(mine);
  }
  return out;
}

export interface NetNode {
  id: string;
  col: number;
  row: number;
}

/**
 * Раскладка сети по столбцам: столбец — длина самой длинной цепочки предшественников (по числу связей),
 * в столбце — по раннему началу. Циклы не ломают раскладку: оставшиеся работы — в последний столбец.
 */
export function networkLayout(v: View, only?: Set<string> | null): { nodes: NetNode[]; cols: number } {
  const acts = v.activities.filter((a) => !only || only.has(a.id));
  const ids = new Set(acts.map((a) => a.id));
  const indeg = new Map<string, number>(acts.map((a) => [a.id, 0]));
  const succs = new Map<string, string[]>();
  for (const r of v.relations) {
    if (!ids.has(r.predId) || !ids.has(r.succId) || r.predId === r.succId) continue;
    indeg.set(r.succId, (indeg.get(r.succId) ?? 0) + 1);
    (succs.get(r.predId) ?? succs.set(r.predId, []).get(r.predId)!).push(r.succId);
  }
  const col = new Map<string, number>();
  const queue = acts.filter((a) => !indeg.get(a.id)).map((a) => a.id);
  for (const id of queue) col.set(id, 0);
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i]!;
    for (const s of succs.get(id) ?? []) {
      col.set(s, Math.max(col.get(s) ?? 0, col.get(id)! + 1));
      const d = indeg.get(s)! - 1;
      indeg.set(s, d);
      if (d === 0) queue.push(s);
    }
  }
  let maxCol = 0;
  for (const c of col.values()) maxCol = Math.max(maxCol, c);
  const placed = new Set(queue);
  const rest = acts.filter((a) => !placed.has(a.id));
  if (rest.length) maxCol++;
  for (const a of rest) col.set(a.id, maxCol);
  const byCol = new Map<number, typeof acts>();
  for (const a of acts) {
    const c = col.get(a.id) ?? 0;
    (byCol.get(c) ?? byCol.set(c, []).get(c)!).push(a);
  }
  const nodes: NetNode[] = [];
  for (const [c, list] of byCol) {
    list.sort(
      (a, b) =>
        (a.dates?.es ?? '').localeCompare(b.dates?.es ?? '') ||
        a.sortOrder - b.sortOrder ||
        a.code.localeCompare(b.code, 'ru', { numeric: true }),
    );
    list.forEach((a, row) => {
      nodes.push({ id: a.id, col: c, row });
    });
  }
  return { nodes, cols: maxCol + 1 };
}
